import { Box, CircularProgress, Skeleton, styled, useTheme } from '@mui/material';
import LoadingSkeleton from 'components/LoadingSkeleton';
import http from 'config/http';
import useIsMobileView from 'hooks/useIsMobileView';
import useNotify from 'hooks/useNotify';
import React, { useEffect, useMemo, useState } from 'react'
import Chart from "react-apexcharts";
import { useSearchParams } from 'react-router-dom';
import { GetPortfolioChart } from 'store/api/wallet';
import { formatPrice } from 'utils/helpers/numbers';


function ChartSpot() {
  const [searchParams] = useSearchParams();
  const isMobileView = useIsMobileView();
  const notify = useNotify()
  const theme = useTheme();

  const transfer = searchParams.get("transfer");

  const [portfolioChart, setPortfolioChart] = useState([]);
  const [loading, setLoading] = useState(true);

  const chartData = useMemo(() => {
    const data = portfolioChart?.creditDetail?.map((item) => ({
      percent: item.usdtPercent,
      label: item.name,
      value: item.totalBalance,
    }));

    return data || [];
  }, [portfolioChart]);

  const chartDataInsides = useMemo(() => {
    const labels = chartData?.map((data) => data.label);
    const percents = chartData?.map((data) => data.percent);
    const values = chartData?.map((data) => data.value);

    return { values, percents, labels };
  }, [chartData]);


  const chartOptions = useMemo(() => {
    const maxVisible = 3;
    const visibleLabels = chartDataInsides?.labels.slice(0, maxVisible);

    const largestItemName = chartData?.length
      ? chartData.reduce((prev, current) =>
        current.percent > prev.percent ? current : prev
      ).label
      : null;

    return {
      theme: {
        palette: "palette1",
        monochrome: {
          enabled: false,
          shadeTo: "dark",
          shadeIntensity: 0.55,
        },
      },
      stroke: {
        show: false,
      },
      tooltip: {
        enabled: true,
        custom: function ({ seriesIndex, w }) {
          return `<div>
                      <span style="padding:5px;">
                        ${w.globals.labels[seriesIndex]}
                      </span>
                    </div>`;
        },
      },
      series: [
        {
          name: chartDataInsides?.labels,
          data: chartDataInsides?.values,
        },
      ],
      dataLabels: {
        enabled: false,
      },
      labels: chartDataInsides?.labels,
      legend: {
        show: true,
        fontSize: "14px",
        fontWeight: 400,
        width: 250,
        labels: {
          colors: theme.palette.common.white,
          useSeriesColors: false,
        },
        formatter: function (val, opts) {
          const index = opts.seriesIndex;
          if (visibleLabels.includes(val)) {
            return [val, " : ", formatPrice(chartData[index].value)];
          } if (index === 4) {
            return "..."
          }
          return "";
        },
        position: isMobileView ? "bottom" : "right",
      },
      chart: {
        height: 100,
        type: "donut",
        background: undefined,
        events: {
          mounted: (chart, config) => {
            chart.toggleSeries(largestItemName);
            chart.highlightSeries(largestItemName);
          },
        },
        zoom: {
          selection: {
            border: theme.palette.common.white,
          },
        },
      },
      plotOptions: {
        pie: {
          labels: {
            enabled: true,
            formatter: function (val) {
              return val + "%";
            },
          },
          expandOnClick: true,
          donut: {
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "10px",
                fontWeight: 700,
                color: theme.palette.common.white,
                offsetY: -10,
                formatter: function (val) {
                  return val;
                },
              },
              value: {
                show: true,
                fontSize: "14px",
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 600,
                color: theme.palette.common.white,
                offsetY: 2,
                formatter: function (val) {
                  return [val + "%"];
                },
              },
            },
          },
        },
      },
    };
  }, [chartData, theme, chartDataInsides])

  const getPortfolioChart = async () => {
    try {
      setLoading(true)
      const { data } = await http.get(GetPortfolioChart(1));
      setPortfolioChart(data.data);
    } catch (error) {
      notify(error?.response.data.message, "error");
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    getPortfolioChart();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer]);

  return loading ? <LoadingSkeleton sx={{ height: "150px" }} variant='rounded' width={"100%"} /> :
    <ChartWrapper>
      <Chart
        label={chartDataInsides?.labels}
        series={chartDataInsides?.percents}
        options={chartOptions}
        type={"donut"}
        width={"450px"}
        height={"150px"}
      />
    </ChartWrapper>

}


const ChartWrapper = styled(Box)(() => ({
  ".apexcharts-legend-series:has(.apexcharts-legend-text:empty)": {
    display: "none !important"
  }
}))
export default ChartSpot